const StorageTypes = require('../jsdoc-types'); // eslint-disable-line no-unused-vars

/**
 * Creates a storage decorator that adds key and/or value validation.
 *
 * The returned decorator wraps a Storage-compatible object and enforces
 * validation rules on keys and/or values before delegating operations to
 * the underlying storage.
 *
 * Validator functions must throw (typically a TypeError) when validation
 * fails. If validation succeeds, they must return normally without
 * returning any value.
 *
 * If no validators are provided, the decorator is a no-op and the original
 * storage instance is returned unchanged.
 *
 * @param {Object} validators
 * Validation configuration object.
 *
 * @param {Function} [validators.validateKey]
 * Function that validates storage keys.
 * Must throw if the key is invalid.
 *
 * @param {Function} [validators.validateValue]
 * Function that validates storage values.
 * Must throw if the value is invalid.
 *
 * @returns {StorageTypes.StorageDecorator}
 * A decorator function that applies validation behavior to a storage instance.
 */
function create({ validateKey, validateValue }) {
  return function decorate(baseStorage) {
    if (!validateKey && !validateValue) {
      // no-op decorator
      return baseStorage;
    }

    return {
      get length() {
        return baseStorage.length;
      },

      key(n) {
        return baseStorage.key(n);
      },

      getItem(key) {
        if (validateKey) validateKey(key);
        return baseStorage.getItem(key);
      },

      setItem(key, value) {
        if (validateKey) validateKey(key);
        if (validateValue) validateValue(value);
        return baseStorage.setItem(key, value);
      },

      removeItem(key) {
        if (validateKey) validateKey(key);
        return baseStorage.removeItem(key);
      },

      clear() {
        return baseStorage.clear();
      },
    };
  };
}

module.exports = {
  create,
};
